import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Select, Checkbox, Layout, TextField, FormLayout, Form, Page, Card, Banner, Button, Tooltip, Box, BlockStack, Modal, RadioButton, Text, InlineStack, Tabs, Link } from '@shopify/polaris';
import { LabelPrinterIcon, TransactionIcon, XCircleIcon } from '@shopify/polaris-icons';
import { useUrl, handleSaveWithSecondPayload } from '../../shared/util/hanldesave.js';
import { useLookupStore } from '../../context/useStore.js';
import CompanySelect from '../../shared/components/companySelect.jsx';
import { getCurrenctDatetimeYYYMMDD, getRequest, alphabetize } from '../../shared/components/functions.jsx';
import DetailFileBrowser from '../../shared/components/files/DetailFileBrowser.jsx';
import { validateSchema } from '../../helpers/schemas/schemasHelpers.js';
import InventoryCard from './components/InventoryCard.jsx';
import QuickStockAdjust from './components/QuickStockAdjust.jsx';
import BomCard from './components/BomCard.jsx';
import TechDescCard from './components/TechDescCard.jsx';
import ProductSalesHistory from './components/ProductSalesHistory.jsx';
import DropboxPartFilesLookup from './components/DropboxPartFilesLookup.jsx';
import PurchaseHistoryModal from './components/PurchaseHistoryModal.jsx';
import { TheSaveBar } from '../../shared/components/theSaveBar.jsx';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { TabTitle } from '../../shell/helmet.jsx';
import { getSchema } from '../../shared/formSchemas.js';
import { useToggle } from '../../shared/useToogle.js';
import EventTimeLine from '../../shared/components/events/eventTimeLine.jsx';
import { CardHeading } from '../../shared/components/cards/CardHeading.jsx';
import { DoublesModal } from './components/DoublesModal.jsx';
import DuplicateProductModal from './components/DuplicateProductModal.jsx';
import { LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip as TooltipRecharts, Legend } from 'recharts';

/*
  FIXME:  
    - ProductName is often not set in access. How should this be handled?
*/

export default function ProductPage() {
  let { id } = useParams();
  const pagination = useRecordPagination('products', id, 'product');
  const navigate = useNavigate();
  let url = useUrl();
  const { units, producttypes, machinebrands, machinetypes, pickingplaces } = useLookupStore();
  const [initialProduct, setInitialProduct] = useState('');
  const [product, setProduct] = useState('');
  const [toast, setToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [company, setCompany] = useState('');
  //added state for new component
  const [inventories, setInventories] = useState([]);
  const [initialInventories, setInitialInventories] = useState([]);
  const [inventoriesDirty, setInventoriesDirty] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [doubles, setDoubles] = useState([]);
  const [doublesModalOpen, setDoublesModalOpen] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [selectedInventoryIndex, setSelectedInventoryIndex] = useState(0);
  const [matchingPrices, setMatchingPrices] = useToggle(true);
  const [rate, setRate] = useState('1.54');
  const pageTitle = id === 'new' ? 'New product' : product.ProductNameNL;
  const productSchema = getSchema('product', url);

  const [invoicedHistory, setInvoicedHistory] = useState([]);
  const [purchasePipeline, setPurchasePipeline] = useState({ summary: null, lines: [] });
  const [groupInvoicedHistoryByYear, setGroupInvoicedHistoryByYear] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  // Dropbox PDF/STEP lookup: optional right after creating a product, and a remount key to reload the documents
  const [searchDropboxAfterSave, setSearchDropboxAfterSave] = useState(false);
  const [dropboxAutoRunFor, setDropboxAutoRunFor] = useState(null);
  const [documentsKey, setDocumentsKey] = useState(0);
  const previousIdRef = useRef(id);
  const [purchaseHistoryOpen, setPurchaseHistoryOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const tabs = [
    { id: 'general', content: 'General', accessibilityLabel: 'General product information' },
    { id: 'sales-history', content: 'Sales History', accessibilityLabel: 'Sales and invoice history for this product' },
    { id: 'bom', content: 'Bill of Materials', accessibilityLabel: 'Bill of Materials' },
    { id: 'tech-desc', content: 'Technical Description', accessibilityLabel: 'Technical Description for Quotations' }
  ];

  const handleTabChange = useCallback((selectedTabIndex) => setSelectedTab(selectedTabIndex), []);

  const handleRateChange = (e) => {
    const r = parseFloat(e);
    setRate(e);
    setProduct(
      (product) => ({
        ...product,
        ['Eenheids verkoopprijs']: (Math.round(parseFloat(product['Eenheidsaankoopprijs']) * r * 100) / 100).toString(),
        ['Datum  bijw_ VP']: getCurrenctDatetimeYYYMMDD() //eslint-disable-line
      }),
      setIsDirty(true)
    );
  };

  const handleChange = (value, ID) => {
    /*
        if price is changed then the bijw_AP and bijw_VP need to be set to the current date
        this is not handled directly in the database
    */
    if (ID === 'Kg') value = value.replace(',', '.');

    if (ID === 'Eenheidsaankoopprijs') {
      if (matchingPrices) {
        setProduct(
          (product) => ({
            ...product,
            Eenheidsaankoopprijs: value,
            ['Eenheids verkoopprijs']: (Math.round(parseFloat(value) * parseFloat(rate) * 100) / 100).toString(), //eslint-disable-line
            ['Datum  bijw_ VP']: getCurrenctDatetimeYYYMMDD(), //eslint-disable-line
            ['Datum bijw_AP']: getCurrenctDatetimeYYYMMDD() //eslint-disable-line
          }),
          setIsDirty(true)
        );
      } else {
        setProduct((product) => ({
          ...product,
          Eenheidsaankoopprijs: value,
          ['Datum bijw_AP']: getCurrenctDatetimeYYYMMDD() //eslint-disable-line
        }));
        let percent = parseFloat(product['Eenheids verkoopprijs']) / parseFloat(value);
        setRate(Math.round(percent * 100) / 100);
        setIsDirty(true);
      }
      return;
    }

    if (ID === 'Eenheids verkoopprijs') {
      if (matchingPrices) {
        setProduct(
          (product) => ({
            ...product,
            Eenheidsaankoopprijs: (Math.round((parseFloat(value) / parseFloat(rate)) * 100) / 100).toString(),
            ['Eenheids verkoopprijs']: value,
            ['Datum  bijw_ VP']: getCurrenctDatetimeYYYMMDD(), //eslint-disable-line
            ['Datum bijw_AP']: getCurrenctDatetimeYYYMMDD() //eslint-disable-line
          }),
          setIsDirty(true)
        );
      } else {
        setProduct(
          (product) => ({
            ...product,
            ['Eenheids verkoopprijs']: value,
            ['Datum  bijw_ VP']: getCurrenctDatetimeYYYMMDD() //eslint-disable-line
          }),
          setIsDirty(true)
        );
        let percent = parseFloat(value) / parseFloat(product['Eenheidsaankoopprijs']) || 0;
        setRate(Math.round((percent * 100) / 100).toString());
      }
      return;
    }

    /*
      Auto update product name languages
    */
    if (ID === 'ProductNameNL' && id === 'new') {
      setProduct((product) => ({ ...product, ProductNameFR: value, ProductNameDE: value, ProductNameUK: value }), setIsDirty(true));
    }

    if (ID === 'OnderdelenTypeID') {
      setProduct((product) => ({ ...product, OnderdelenTypeID: value }), setIsDirty(true));
    }

    if (ID === 'MachineMerkId') {
      setProduct((product) => ({ ...product, MachineMerkId: value }), setIsDirty(true));
    }

    if (ID === 'MachineTypeId') {
      setProduct((product) => ({ ...product, MachineTypeId: value }), setIsDirty(true));
    }

    setProduct((product) => ({ ...product, [ID]: value }), setIsDirty(true));
  };

  const handleBlur = (e, ID) => {
    const value = e.target.value;
    if (value) setProduct((product) => ({ ...product, [ID]: Math.round(Number(value) * 100) / 100 }));
  };

  const handleDiscard = () => {
    setProduct(initialProduct);
    setInventories(initialInventories);
    setIsDirty(false);
    setInventoriesDirty(false);
    setFormSubmitting(false);
  };

  const handleFormSubmit = async () => {
    setFormSubmitting(true);

    product.Eenheidsaankoopprijs = parseFloat(product.Eenheidsaankoopprijs) || 0;
    product['Eenheids verkoopprijs'] = parseFloat(product['Eenheids verkoopprijs']) || 0;

    if (product.Eenheidsaankoopprijs !== initialProduct.Eenheidsaankoopprijs) {
      product.Eenheidsaankoopprijs = Math.round(product.Eenheidsaankoopprijs * 100) / 100;
    }
    if (product['Eenheids verkoopprijs'] !== initialProduct['Eenheids verkoopprijs']) {
      product['Eenheids verkoopprijs'] = Math.round(product['Eenheids verkoopprijs'] * 100) / 100;
    }

    if (!product.EenheidAantal) product.EenheidAantal = 0;

    setProduct(product);

    if (id !== 'new' && !inventories.find((i) => i.Stockplaats === 1)) {
      alert('Attention: the inventory line for Kast is missing. Please add it to the inventory (even if quantity is 0).');
      return setFormSubmitting(false);
    }

    const errorInSchema = await validateSchema(productSchema, productSchema.cast(product));
    if (!errorInSchema) {
      if (!product.Productref && !product.ProductNameNL) {
        handleSave(product);
        return setFormSubmitting(false);
      }

      //check for doubles: same or similar reference, or the same name
      const doublesQuery = new URLSearchParams({ product: id, ref: product.Productref || '', name: product.ProductNameNL || '', supplier: product.SupplierID ?? '' });
      const levels = await getRequest(`/api/products/doubles?${doublesQuery}`);
      if (levels && ['lvlOne', 'lvlTwo', 'lvlThree', 'lvlFour'].some((level) => levels[level]?.length > 0)) {
        setDoubles(levels);
        setDoublesModalOpen(true);
        return setFormSubmitting(false);
      } else {
        handleSave(product);
      }
    } else {
      console.log('Product:', product);
      alert(errorInSchema);
    }
    setFormSubmitting(false);
  };

  const handleSave = async (updatedProduct) => {
    let productData = updatedProduct || product;
    await handleSaveWithSecondPayload(
      id,
      'products',
      productData.ProductID,
      'ProductID',
      productSchema.cast(productData),
      inventories,
      setProduct,
      setInitialProduct,
      `inventory/dump/lines/${id}`,
      setInventories,
      setInitialInventories,
      isDirty,
      setIsDirty,
      inventoriesDirty,
      setInventoriesDirty,
      setToast,
      setErrorToast,
      navigate
    );
  };

  useEffect(() => {
    async function fetchData() {
      if (id === 'new') {
        setProduct(productSchema.default());
        setInitialProduct(productSchema.default());
      } else {
        const data = await getRequest(`/api/products/${id}`);
        if (!data) {
          setProduct(productSchema.default());
          setInitialProduct(productSchema.default());
          return;
        }
        const product = data;
        product['Datum bijw_AP'] = product['Datum bijw_AP'] !== null ? String(product['Datum bijw_AP']).substring(0, 22).replace('T', ' ') : null;
        product['Datum  bijw_ VP'] = product['Datum  bijw_ VP'] !== null ? String(product['Datum  bijw_ VP']).substring(0, 22).replace('T', ' ') : null;
        if (product.Eenheidsaankoopprijs !== 0) {
          let percent = product['Eenheids verkoopprijs'] / product.Eenheidsaankoopprijs;
          setRate(Math.round(percent * 100) / 100);
        } else {
          // reset the rate when stepping to a product without purchase price
          setRate('1.54');
        }
        setProduct(data);
        setInitialProduct(data);

        const [invoicedHistoryData, purchasePipelineData] = await Promise.all([
          getRequest(`/api/metrics/products_invoiced/${id}?groupBy=${groupInvoicedHistoryByYear ? 'YEAR' : 'MONTH'}`),
          getRequest(`/api/products/${id}/purchase-pipeline`)
        ]);
        setInvoicedHistory(invoicedHistoryData);
        if (purchasePipelineData) setPurchasePipeline(purchasePipelineData);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, groupInvoicedHistoryByYear]);

  useEffect(() => {
    // the page stays mounted when a new product is saved (/products/new -> /products/:id)
    if (previousIdRef.current === 'new' && id !== 'new' && searchDropboxAfterSave) setDropboxAutoRunFor(id);
    previousIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDropboxFilesAttached = useCallback(() => setDocumentsKey((key) => key + 1), []);

  useEffect(() => {
    if (id !== 'new') getInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      setCompany(await getRequest(`/api/companies/${product.SupplierID}`));
    }
    if (product.SupplierID) fetchData();
  }, [product.SupplierID]);

  const getInventory = async () => {
    const data = await getRequest(`/api/inventory/${id}`);
    //new comp
    setInitialInventories(data);
    setInventories(data);
  };

  const getPickingplaceName = (stockplaatsId) => {
    const place = pickingplaces?.find((p) => p.counter === stockplaatsId);
    return place?.pickingplace || `Location ${stockplaatsId}`;
  };

  const printLabelWithInventory = async (inventory) => {
    const stockplaatsName = getPickingplaceName(inventory?.Stockplaats);
    const payload = {
      id: id,
      name: product.ProductNameNL,
      ref: product.Productref || '',
      supplier: company.NAAM,
      rack: inventory?.Rack || '',
      legplank: inventory?.Legplank || '',
      stockplaats: stockplaatsName
    };
    const response = await fetch('/api/products/label/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.download = `product_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      console.error('Error:', response.status, response.statusText);
    }
  };

  const printLabel = async () => {
    // If multiple inventory locations, show modal to select
    if (inventories.length > 1) {
      setSelectedInventoryIndex(0);
      setLabelModalOpen(true);
      return;
    }

    // If single or no inventory, print directly
    const inventory = inventories[0] || null;
    await printLabelWithInventory(inventory);
  };

  const handleLabelModalConfirm = async () => {
    const selectedInventory = inventories[selectedInventoryIndex];
    setLabelModalOpen(false);
    await printLabelWithInventory(selectedInventory);
  };

  return (
    <Page
      title={pageTitle}
      subtitle={id === 'new' ? '' : '#' + id}
      fullWidth
      primaryAction={id !== 'new' ? { content: 'New product', url: '/products/new' } : null}
      secondaryActions={
        id !== 'new' ? (
          <InlineStack gap="200">
            <Button onClick={() => setDuplicateModalOpen(true)} disabled={isDirty || inventoriesDirty || formSubmitting}>
              Duplicate
            </Button>
            <Button icon={LabelPrinterIcon} onClick={printLabel} />
          </InlineStack>
        ) : (
          []
        )
      }
      pagination={pagination}
    >
      <TabTitle title={pageTitle} />
      <TheSaveBar isDirty={isDirty || inventoriesDirty} handleSave={handleFormSubmit} handleDiscard={handleDiscard} savingResult={formSubmitting} />
      {product.Discontinued && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" title="This product is discontinued!" />
        </Box>
      )}
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} toastContent="Product saved" />
      <DoublesModal modalOpen={doublesModalOpen} setModalOpen={setDoublesModalOpen} doubles={doubles} handleDiscard={handleDiscard} handleSave={handleSave} />
      <DuplicateProductModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        productId={id}
        productName={product.ProductNameNL}
        isDiscontinued={product.Discontinued}
        onDuplicated={(data) => {
          setDuplicateModalOpen(false);
          navigate(`/products/${data.product.ProductID}`);
        }}
      />

      {/* Stock Location Selection Modal for Label Printing */}
      <Modal
        open={labelModalOpen}
        onClose={() => setLabelModalOpen(false)}
        title="Select stock location for label"
        primaryAction={{
          content: 'Print Label',
          onAction: handleLabelModalConfirm
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setLabelModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              This product has multiple stock locations. Select which location to print on the label:
            </Text>
            {inventories.map((inv, index) => (
              <Box
                key={index}
                padding="300"
                background={selectedInventoryIndex === index ? 'bg-surface-selected' : 'bg-surface-secondary'}
                borderRadius="200"
                borderWidth="025"
                borderColor={selectedInventoryIndex === index ? 'border-emphasis' : 'border'}
              >
                <InlineStack align="space-between" blockAlign="center">
                  <RadioButton
                    label={
                      <BlockStack gap="100">
                        <Text as="span" fontWeight="semibold">
                          {getPickingplaceName(inv.Stockplaats)}
                        </Text>
                        <Text as="span" tone="subdued" variant="bodySm">
                          Rack: {inv.Rack || '-'} | Shelf: {inv.Legplank || '-'} | Qty: {inv.aantal || 0}
                        </Text>
                      </BlockStack>
                    }
                    checked={selectedInventoryIndex === index}
                    onChange={() => setSelectedInventoryIndex(index)}
                  />
                </InlineStack>
              </Box>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Quick Stock Adjust - Mobile (shown at top of page) */}
      {id !== 'new' && (
        <Box paddingBlockEnd="200">
          <QuickStockAdjust inventories={inventories} setInventories={setInventories} product={product} pickingplaces={pickingplaces} refetchInventory={getInventory} showOn="mobile" />
        </Box>
      )}

      {/* Tab Navigation */}
      <div style={{ marginTop: '-8px' }}>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
          {/* General Tab */}
          {selectedTab === 0 && (
            <Box paddingBlockStart="300">
              <Layout>
                {/* Left column - Product info, Pricing, Inventory */}
                <Layout.Section variant="oneHalf">
                  <BlockStack gap="400">
                    {/* Basic Product Info */}
                    <Card>
                      <Form onSubmit={handleFormSubmit} autoComplete="off">
                        <FormLayout>
                          <CompanySelect isClearable={false} value={product.SupplierID} id="SupplierID" onChange={handleChange} label="Supplier" isDisabled={formSubmitting} />
                          <FormLayout.Group>
                            <TextField
                              id="Productref"
                              maxLength={50}
                              showCharacterCount
                              autoComplete="off"
                              label="Reference"
                              value={product.Productref || ''}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                            <TextField
                              id="PreviousReference"
                              maxLength={1000}
                              autoComplete="off"
                              label="Previous Reference"
                              value={product.PreviousReference || ''}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                          </FormLayout.Group>
                          <TextField
                            id="ProductNameNL"
                            maxLength={60}
                            showCharacterCount
                            autoComplete="off"
                            label="Product name (dutch)"
                            value={product.ProductNameNL || ''}
                            onChange={handleChange}
                            disabled={formSubmitting}
                          />
                          <FormLayout.Group>
                            <TextField id="ProductNameFR" maxLength={60} autoComplete="off" label="French" value={product.ProductNameFR || ''} onChange={handleChange} disabled={formSubmitting} />
                            <TextField id="ProductNameDE" maxLength={60} autoComplete="off" label="German" value={product.ProductNameDE || ''} onChange={handleChange} disabled={formSubmitting} />
                            <TextField id="ProductNameUK" maxLength={60} autoComplete="off" label="English" value={product.ProductNameUK || ''} onChange={handleChange} disabled={formSubmitting} />
                          </FormLayout.Group>
                          <FormLayout.Group>
                            <Select
                              id="EenheidID"
                              label="Unit"
                              value={parseInt(product.EenheidID) || ''}
                              options={units.sort((a, b) => alphabetize(a, b, 'Eenheid')).map((unit) => ({ label: unit.Eenheid, value: unit.EenheidID }))}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                            <TextField id="EenheidAantal" label="Qty/unit" autoComplete="off" type="number" value={String(product.EenheidAantal)} onChange={handleChange} disabled={formSubmitting} />
                            <TextField id="ExternalQrUrl" autoComplete="off" label="External QR URL" value={product.ExternalQrUrl || ''} onChange={handleChange} disabled={formSubmitting} />
                          </FormLayout.Group>
                        </FormLayout>
                      </Form>
                    </Card>

                    {/* Pricing Card */}
                    <Card>
                      <CardHeading title="Pricing" actions={id !== 'new' ? [{ content: 'Purchase history & invoices', onAction: () => setPurchaseHistoryOpen(true) }] : undefined} />
                      <Form onSubmit={handleFormSubmit} autoComplete="off">
                        <FormLayout>
                          <FormLayout.Group>
                            <TextField
                              id="Eenheidsaankoopprijs"
                              type="number"
                              label="Purchase price"
                              helpText={`Updated: ${String(product['Datum bijw_AP']).substring(0, 10)}`}
                              prefix="€"
                              value={product.Eenheidsaankoopprijs}
                              onChange={handleChange}
                              onBlur={(e) => handleBlur(e, 'Eenheidsaankoopprijs')}
                              disabled={formSubmitting}
                            />
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {matchingPrices && <TextField id="rate" label="Rate" type="number" value={rate} onChange={handleRateChange} disabled={formSubmitting} />}
                              <div style={{ padding: '20px 8px 0' }}>
                                <Tooltip content={matchingPrices ? 'Disable price link' : 'Enable price link'}>
                                  <Button disabled={formSubmitting} onClick={() => setMatchingPrices()} icon={matchingPrices ? XCircleIcon : TransactionIcon} />
                                </Tooltip>
                              </div>
                            </div>
                            <TextField
                              id="Eenheids verkoopprijs"
                              type="number"
                              label="Selling price"
                              helpText={`Updated: ${String(product['Datum  bijw_ VP']).substring(0, 10)}`}
                              prefix="€"
                              value={product['Eenheids verkoopprijs']}
                              onChange={handleChange}
                              onBlur={(e) => handleBlur(e, 'Eenheids verkoopprijs')}
                              disabled={formSubmitting}
                            />
                          </FormLayout.Group>
                          <FormLayout.Group>
                            <TextField
                              id="ServiceFee"
                              type="number"
                              label="Service Fee"
                              prefix="€"
                              value={product.ServiceFee || ''}
                              onChange={handleChange}
                              onBlur={(e) => handleBlur(e, 'ServiceFee')}
                              disabled={formSubmitting}
                            />
                            <TextField id="Kg" autoComplete="off" label="Weight (kg)" type="number" value={product.Kg || ''} onChange={handleChange} disabled={formSubmitting} />
                            <TextField id="HSCode" autoComplete="off" label="HS code" value={product.HSCode || ''} onChange={handleChange} disabled={formSubmitting} />
                          </FormLayout.Group>
                        </FormLayout>
                      </Form>
                    </Card>

                    {id !== 'new' && <PurchaseHistoryModal open={purchaseHistoryOpen} onClose={() => setPurchaseHistoryOpen(false)} productId={id} />}

                    {/* Quick Stock Adjust - Desktop */}
                    {id !== 'new' && (
                      <QuickStockAdjust inventories={inventories} setInventories={setInventories} product={product} pickingplaces={pickingplaces} refetchInventory={getInventory} showOn="desktop" />
                    )}

                    {/* Inventory Card */}
                    <InventoryCard
                      handleFormSubmit={handleFormSubmit}
                      id={id}
                      product={product}
                      inventories={inventories}
                      setInventories={setInventories}
                      setInventoriesDirty={setInventoriesDirty}
                      errorToast={errorToast}
                      sectioned
                    />
                  </BlockStack>
                </Layout.Section>

                {/* Right column - Classification, Notes, Documents, History */}
                <Layout.Section variant="oneHalf">
                  <BlockStack gap="400">
                    {/* Classification */}
                    <Card>
                      <CardHeading title="Classification" />
                      <Form onSubmit={handleFormSubmit} autoComplete="off">
                        <FormLayout>
                          <Checkbox id="Discontinued" label="Discontinued" checked={product.Discontinued} onChange={handleChange} disabled={formSubmitting} />
                          <FormLayout.Group>
                            <Select
                              id="OnderdelenTypeID"
                              label="Product group"
                              placeholder="Select..."
                              value={product.OnderdelenTypeID ? String(product.OnderdelenTypeID) : undefined}
                              options={producttypes.map((type) => ({ label: type.OnderdelenType, value: String(type.OnderdelenTypeID) }))}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                            <Select
                              id="MachineMerkId"
                              placeholder="Select..."
                              label="Machine Brand"
                              value={product.MachineMerkId ? String(product.MachineMerkId) : undefined}
                              options={machinebrands.map((brand) => ({ label: brand.merk, value: String(brand.counter) }))}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                            <Select
                              placeholder="Select..."
                              id="MachineTypeId"
                              label="Machine Type"
                              value={product.MachineTypeId ? String(product.MachineTypeId) : undefined}
                              options={machinetypes.map((type) => ({ label: type.Type, value: String(type.ID) }))}
                              onChange={handleChange}
                              disabled={formSubmitting}
                            />
                          </FormLayout.Group>
                        </FormLayout>
                      </Form>
                    </Card>

                    {/* Notes */}
                    <Card>
                      <CardHeading title="Notes" />
                      <Form onSubmit={handleFormSubmit} autoComplete="off">
                        <FormLayout>
                          <TextField
                            id="Omschrijving"
                            autoComplete="off"
                            value={product.Omschrijving || ''}
                            onChange={handleChange}
                            multiline={8}
                            placeholder="Internal notes..."
                            label="Internal"
                            disabled={formSubmitting}
                          />
                          <TextField
                            id="Externalnote"
                            autoComplete="off"
                            value={product.Externalnote || ''}
                            onChange={handleChange}
                            multiline={5}
                            placeholder="Visible to customers..."
                            label="External"
                            disabled={formSubmitting}
                          />
                        </FormLayout>
                      </Form>
                    </Card>

                    {/* Documents */}
                    {id !== 'new' ? (
                      <div>
                        <DropboxPartFilesLookup productId={id} autoRun={dropboxAutoRunFor === id} onAttached={handleDropboxFilesAttached} />
                        <DetailFileBrowser
                          key={`${id}-${documentsKey}`}
                          docTypeId="25"
                          resourceId={id}
                          Klantnummer={company.Klantnummer}
                          docNumber={product.ProductID}
                          FIRNR={company.FIRNR}
                          docType="part"
                          heightPxs="350px"
                        />
                      </div>
                    ) : (
                      <Card>
                        <CardHeading title="Documents" />
                        <BlockStack gap="200">
                          <Text as="p" tone="subdued">
                            Upload available after saving.
                          </Text>
                          <Checkbox
                            label="Search Dropbox for the PDF & STEP files after saving"
                            helpText="Files named exactly like the product name or reference are attached to the product."
                            checked={searchDropboxAfterSave}
                            onChange={setSearchDropboxAfterSave}
                          />
                        </BlockStack>
                      </Card>
                    )}

                    {/* Incoming Purchases */}
                    <Card>
                      <CardHeading title="Incoming Purchases" />
                      {purchasePipeline?.summary ? (
                        <BlockStack gap="200">
                          <InlineStack gap="300">
                            <Text as="p" variant="bodySm">
                              In purchase: <strong>{purchasePipeline.summary.totalInPurchase || 0}</strong>
                            </Text>
                            <Text as="p" variant="bodySm">
                              Ordered: <strong>{purchasePipeline.summary.totalOrdered || 0}</strong>
                            </Text>
                            <Text as="p" variant="bodySm">
                              Already received: <strong>{purchasePipeline.summary.totalReceived || 0}</strong>
                            </Text>
                          </InlineStack>
                          {Array.isArray(purchasePipeline.lines) && purchasePipeline.lines.length > 0 ? (
                            <div style={{ maxHeight: '160px', overflow: 'auto' }}>
                              {purchasePipeline.lines.slice(0, 6).map((line) => (
                                <div key={line.BestellingLijnID} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                                  <div>
                                    <Link removeUnderline url={`/purchases/${line.BestellingID}`}>
                                      PO #{line.Bestellingnummer}
                                    </Link>{' '}
                                    <Text as="span" tone="subdued">
                                      ({line.SupplierName || '-'}
                                      {line.DeliveryDate ? `, due ${String(line.DeliveryDate).substring(0, 10)}` : ''})
                                    </Text>
                                  </div>
                                  <Text as="span">{line.RemainingQty}</Text>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Text as="p" tone="subdued">
                              No open purchase orders for this product.
                            </Text>
                          )}
                        </BlockStack>
                      ) : (
                        <Text as="p" tone="subdued">
                          Loading purchase data...
                        </Text>
                      )}
                    </Card>

                    {/* Sales History */}
                    <Card>
                      <CardHeading title="Sales History" />
                      {invoicedHistory.length > 0 ? (
                        <div style={{ height: '280px' }}>
                          <Checkbox checked={groupInvoicedHistoryByYear} onChange={setGroupInvoicedHistoryByYear} label="Group by year" />
                          <ResponsiveContainer width="100%" height="90%">
                            <LineChart data={invoicedHistory}>
                              <Legend />
                              <CartesianGrid strokeDasharray="1 5" />
                              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <TooltipRecharts />
                              <Line type="monotone" dataKey="invoicedAmount" stroke="#64BE7B" dot={false} />
                              <Line type="monotone" dataKey="salesAmount" stroke="orange" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <Text as="p" tone="subdued">
                          No sales data available
                        </Text>
                      )}
                    </Card>

                    {/* Event Timeline */}
                    <EventTimeLine resourceName="PRODUCT" resourceId={id} />
                  </BlockStack>
                </Layout.Section>
              </Layout>
            </Box>
          )}

          {/* Sales History Tab - Full Width */}
          {selectedTab === 1 && (
            <Box paddingBlockStart="300">
              {id !== 'new' ? (
                <ProductSalesHistory productId={id} />
              ) : (
                <Card>
                  <CardHeading title="Sales History" />
                  <Text as="p" tone="subdued">
                    Sales history becomes available after saving the product.
                  </Text>
                </Card>
              )}
            </Box>
          )}

          {/* BOM Tab - Full Width */}
          {selectedTab === 2 && (
            <Box paddingBlockStart="300">
              {id !== 'new' ? (
                <BomCard productId={id} disabled={formSubmitting} />
              ) : (
                <Card>
                  <CardHeading title="Bill of Materials" />
                  <Text as="p" tone="subdued">
                    BOM available after saving the product.
                  </Text>
                </Card>
              )}
            </Box>
          )}

          {/* Technical Description Tab - Full Width */}
          {selectedTab === 3 && (
            <Box paddingBlockStart="300">
              {id !== 'new' ? (
                <BlockStack gap="400">
                  <Banner tone="info">
                    This is the master technical description for this product. When this product is added to a quotation, the description is copied to the quotation line and can be edited
                    independently per version. Changes here will only apply to newly added quotation lines.
                  </Banner>
                  <TechDescCard
                    productId={id}
                    product={product}
                    onProductChange={async () => {
                      const data = await getRequest(`/api/products/${id}`);
                      if (data) setProduct(data);
                    }}
                  />
                </BlockStack>
              ) : (
                <Card>
                  <CardHeading title="Technical Description" />
                  <Text as="p" tone="subdued">
                    Technical description available after saving the product.
                  </Text>
                </Card>
              )}
            </Box>
          )}
        </Tabs>
      </div>
    </Page>
  );
}
